/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  greaterThan,
  maxStringLengthValidator,
  minNumber,
  requiredNumber,
  requiredObject,
  requiredString,
  ValidatorSchema
} from '@irian-ro/util-forms';
import { ShipmentRequestDtoModel } from '../shipment-request-dto.model';
import { Signal } from '@angular/core';
import { MAXIMUM_DEFAULT_STRING_INPUT_LENGTH } from '@irian-ro/constants';

export interface ShipmentRequestTemplateConfig {
  packageCount: Signal<number> | undefined;
}

export const shipmentRequestTemplateDtoValidators = (
  config: ShipmentRequestTemplateConfig
): ValidatorSchema<ShipmentRequestDtoModel> => {
  return {
    pickup: {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      contact: {
        name: [maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)],
        phoneNumber: [maxStringLengthValidator(20)],
      },
      namedLocationVO: requiredObject,
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        }
      ],
    },
    delivery: {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      contact: {
        name: [maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)],
        phoneNumber: [maxStringLengthValidator(20)],
      },
      namedLocationVO: requiredObject,
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        }
      ],
    },
    packages: {
      height: [requiredNumber, minNumber(1)],
      width: [requiredNumber, minNumber(1)],
      length: [requiredNumber, minNumber(1)],
      weight: [requiredNumber, greaterThan(0)],
      pieces: [requiredNumber, minNumber(1)],
    },
    productCustomsClearanceDTOS: {
      totalWeight: [greaterThan(0)],
    },
    $cross: [
      (shipment: any) => {
        return shipment?.packages && Object.keys(shipment.packages).length ? null : { packageRequired: true };
      },
      (shipment: any) => {
        return shipment?.pickup && (shipment.pickup.namedLocationVO || shipment.pickup.address)
          ? null
          : { pickupLocationRequired: true };
      },
      (shipment: any) => {
        return shipment?.delivery && (shipment.delivery.namedLocationVO || shipment.delivery.address)
          ? null
          : { deliveryLocationRequired: true };
      },
      (shipment) => {
        const totalPackagesForCurrentShipment = Object.values(shipment?.packages || {}).reduce(
          (acc, pkg) => acc + (pkg?.pieces || 0),
          0
        );
        const packageCount =  config.packageCount ? config.packageCount() : undefined;

        if(!packageCount) {
          return null;
        }

        return totalPackagesForCurrentShipment > packageCount ? { packageCountExceeded: true } : null;
      }
    ],
    $omit: [
      {
        field: 'productCustomsClearanceDTOS',
        condition: (value) => true,
      }
    ],
  };
};
